/**
 * Runtime baseURL derivation helpers.
 *
 * Goal: derive backend gateway / OIDC server baseURL from current frontend hostname.
 * Mapping rule (current project):
 *   oaktest.<domain>  -> gatetest.<domain>
 *
 * Note: keep the pathname from the corresponding env URL (e.g. /api/), so API paths remain unchanged.
 */

export function normalizePathname(pathname) {
  if (!pathname) return '/'
  return pathname.endsWith('/') ? pathname : `${pathname}/`
}

export function getEnvUrlPathname(envUrl, fallbackPathname = '/') {
  try {
    const u = new URL(envUrl)
    return normalizePathname(u.pathname || fallbackPathname)
  } catch (e) {
    return normalizePathname(fallbackPathname)
  }
}

/**
 * Host mapping strategy.
 *
 * **Default rule (applies to ANY domain)**:
 * - oaktest.<domain> -> gatetest.<domain>
 * - oak.<domain>     -> gate.<domain>
 *
 * Only keep a small **exception list** for domains whose mapping differs.
 * Example: shjinjia.com.cn uses oak -> gatetest (not gate).
 */
const DEFAULT_SUBDOMAIN_MAP = {
  oaktest: 'gatetest',
  oak: 'gate'
}

const DOMAIN_EXCEPTIONS = []

function splitHostname(hostname) {
  const h = String(hostname || '').trim().toLowerCase()
  if (!h) return null
  const parts = h.split('.').filter(Boolean)
  if (parts.length < 2) return null
  return { parts, first: parts[0], rest: parts.slice(1).join('.') }
}

/**
 * Map a frontend hostname to backend hostname using HOST_MAPPING_RULES.
 * Returns null when no mapping applies.
 */
export function mapOakHostToGateHost(hostname) {
  const info = splitHostname(hostname)
  if (!info) return null

  const { first, rest } = info
  const exception = DOMAIN_EXCEPTIONS.find(r => rest.endsWith(r.domainSuffix))
  const mappedFirst = (exception && exception.subdomainMap && exception.subdomainMap[first]) || DEFAULT_SUBDOMAIN_MAP[first]
  if (!mappedFirst || mappedFirst === first) return null

  return `${mappedFirst}.${rest}`
}

/**
 * Derive a https baseURL from current window.location.hostname, using mapped host + provided pathname.
 * Returns null if current host doesn't match the mapping rule.
 */
export function deriveHttpsBaseURLFromLocation({ pathname = '/', mapHost = mapOakHostToGateHost } = {}) {
  try {
    if (typeof window === 'undefined' || !window.location) return null
    const host = window.location.hostname || ''
    const mappedHost = mapHost(host)
    if (!mappedHost) return null
    return `https://${mappedHost}${normalizePathname(pathname)}`
  } catch (e) {
    return null
  }
}

